import { Component, inject, HostListener, OnDestroy, effect } from '@angular/core';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth/auth.service';
import { InAppNotificationService } from '../../services/notification/in-app-notification.service';
import { NotificationService, NotificationItem } from '../../services/notification/notification.service';
import { RealtimeNotificationService } from '../../services/notification/realtime-notification.service';
import { UserProfileStore } from '../../states/user-profile/user-profile.store';
import { AuthenticationStore } from '../../states/auth/auth.store';
import { ProviderStore } from '../../states/provider/provider.store';
import { providerRoute, providerRoutePath } from '../../utils/provider-route.util';
import { notificationPreview } from '../../utils/in-app-notification.util';

@Component({
  selector: 'app-header',
  standalone: false,
  templateUrl: './header.html',
  styleUrl: './header.css',
})
export class Header implements OnDestroy {
  readonly authService = inject(AuthService);
  readonly authStore = inject(AuthenticationStore);
  readonly userProfileStore = inject(UserProfileStore);
  readonly providerStore = inject(ProviderStore);
  readonly notificationService = inject(NotificationService);
  private readonly inAppNotificationService = inject(InAppNotificationService);
  private readonly realtimeNotificationService = inject(RealtimeNotificationService);
  private router = inject(Router);

  private notifSub: Subscription;

  constructor() {
    this.notifSub = this.notificationService.newNotification$.subscribe(() => {
      this.notifPanelOpen = true;
      this.dropdownOpen = false;
    });

    effect(() => {
      this.userProfileStore.avatar();
      this.avatarLoadFailed = false;
    });

    effect(() => {
      const role = this.userProfileStore.profileRole()?.roleName?.toLowerCase();
      const personGuid = this.userProfileStore.personGuid();
      if (role === 'provider' && personGuid && !this.providerStore.providerGuid()) {
        this.providerStore.loadProviderByPersonGuid(personGuid);
      }
    });

    effect(() => {
      if (this.authStore.isAuthenticated()) {
        void this.realtimeNotificationService.connect();
      } else {
        void this.realtimeNotificationService.disconnect();
      }
    });
  }

  ngOnDestroy(): void {
    this.notifSub.unsubscribe();
  }

  get FullName(): string {
    return this.userProfileStore.fullName() || '';
  }

  get FirstName(): string {
    return this.userProfileStore.firstName() || '';
  }

  get Initials(): string {
    return this.userProfileStore.initials() || '';
  }

  get Avatar(): string {
    return this.userProfileStore.avatar() || '';
  }

  avatarLoadFailed = false;

  get UserRole(): string {
    return this.userProfileStore.roleName() || '';
  }

  get isAuthenticated(): boolean {
    const result = this.authStore.isAuthenticated();
    return result;
  }

  get isEmailVerified(): boolean {
    return this.userProfileStore.isEmailVerified();
  }

  mobileMenuOpen  = false;
  dropdownOpen    = false;
  notifPanelOpen  = false;

  toggleMobileMenu(): void {
    this.mobileMenuOpen = !this.mobileMenuOpen;
  }

  toggleDropdown(): void {
    this.dropdownOpen = !this.dropdownOpen;
    if (this.dropdownOpen) this.notifPanelOpen = false;
  }

  toggleNotifPanel(): void {
    this.notifPanelOpen = !this.notifPanelOpen;
    if (this.notifPanelOpen) {
      this.dropdownOpen = false;
      this.notificationService.markAllRead();
      this.inAppNotificationService.markAllRead().subscribe({ error: () => {} });
    }
  }

  notifIcon(type: NotificationItem['type']): string {
    const map: Record<NotificationItem['type'], string> = {
      success: 'pi pi-check-circle',
      error: 'pi pi-times-circle',
      warning: 'pi pi-exclamation-triangle',
      info: 'pi pi-info-circle',
    };
    return map[type];
  }

  notifColor(type: NotificationItem['type']): string {
    const map: Record<NotificationItem['type'], string> = {
      success: 'var(--success)',
      error: 'var(--destructive)',
      warning: 'var(--warning)',
      info: 'var(--primary-600)',
    };
    return map[type];
  }

  notifPreview(message: string, title?: string): string {
    return notificationPreview(message, 120, title).preview;
  }

  isNotifTruncated(message: string, title?: string): boolean {
    return notificationPreview(message, 120, title).truncated;
  }

  openNotificationsPage(notificationId?: string): void {
    this.notifPanelOpen = false;
    this.router.navigate(['/notifications'], notificationId ? { queryParams: { id: notificationId } } : undefined);
  }

  get profileRoute(): string {
    const userRole = this.userProfileStore.profileRole()?.roleName;
    if (userRole === 'Provider') {
      const providerGuid = this.providerStore.providerGuid();
      return `features/provider/${providerGuid}/profile`;
    }
    return `features/patient/${this.userProfileStore.userProfileGuid()}/profile`;
  }

  get dashboardRoute(): string {
    const userRole = this.userProfileStore.profileRole()?.roleName;
    if (userRole === 'Provider') {
      const providerGuid = this.providerStore.providerGuid();
      return providerGuid ? providerRoutePath(providerGuid, 'dashboard') : '/';
    }
    return `features/patient/${this.userProfileStore.userProfileGuid()}/dashboard`;
  }

  get verificationRoute(): string | null {
    if (!this.isEmailVerified) return null;
    if (this.userProfileStore.profileRole()?.roleName !== 'Provider') return null;
    if (this.providerStore.isVerified()) return null;
    const providerGuid = this.providerStore.providerGuid();
    return providerGuid ? providerRoutePath(providerGuid, 'verification') : null;
  }

  logout(): void {
    this.authStore.logout();
    this.dropdownOpen    = false;
    this.mobileMenuOpen = false;
    //this.router.navigate(['/']);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.user-dropdown-wrapper')) {
      this.dropdownOpen = false;
    }
    if (!target.closest('.notif-panel-wrapper')) {
      this.notifPanelOpen = false;
    }
  }
}
